import React, { ReactNode } from 'react';
import classNames from 'classnames';
import SelectableBase, {
  SelectableRenderData,
  ExtendableSelectableBaseProps,
} from '../SelectableBase';
import ButtonBase from '../ButtonBase';
import {
  ResponsiveValue,
  SizeValue,
  getCssValue,
  getResponsiveValue,
  processStylingProps,
  DeprecatedAndDangerousStylingProps,
} from '../_internal/styling';
import useScreenSize from '../_internal/useScreenSize';
import { DecorativeIconProvider } from '../IconBase';

import styles from './selectableButton.module.scss';
import logger from '../_internal/logger';

type Container = (
  data: {
    children: ReactNode;
    'aria-disabled': boolean;
    'aria-label'?: string;
  } & Omit<SelectableRenderData, 'input'>,
) => JSX.Element;

const XMark = () => (
  <>
    <div className={styles['x-mark-nw']} role="presentation" />
    <div className={styles['x-mark-ne']} role="presentation" />
    <div className={styles['x-mark-se']} role="presentation" />
    <div className={styles['x-mark-sw']} role="presentation" />
  </>
);

interface Props extends DeprecatedAndDangerousStylingProps {
  /**
   * An additional accessible label if the main button contents are insufficient.
   * Most helpful when `status` is `"unavailable"`
   */
  'aria-label'?: string;
  /**
   * Button contents
   */
  children: ReactNode;
  /**
   * A render prop that wraps the selectable button component, allowing for placement above and below the button
   */
  container?: Container;
  /**
   * The color type of the selectable button
   *
   * @default 'default'
   */
  color?: ResponsiveValue<'default' | 'inverse'>;
  /**
   * (Deprecated) Whether or not the button is disabled. Use the `status` prop instead
   *
   * @default false
   * @deprecated Use `status` prop instead
   */
  disabled?: boolean;
  /**
   * An icon to place after the `children`.
   */
  endIcon?: ReactNode;
  /**
   * The minimum width of the selectable button. A `true` value means use the default `min-width`, otherwise specific a value. Defaults to `false` where the button fits the width of the content. This value is responsive and may be configured for multiple screen sizes.
   *
   * @default false
   */
  minWidth?: ResponsiveValue<boolean | SizeValue>;
  /**
   * The size of the button. This value is responsive and may be configured for multiple screen sizes.
   *
   * @deprecated This value is no longer used, and can be removed.
   */
  size?: ResponsiveValue<'large' | 'small'>;
  /**
   * An icon to place before the `children`.
   */
  startIcon?: ReactNode;
  /**
   * Controls the disabled/unavailable state of the button
   *
   * - `enabled` - The button appears visually enabled & can be selected
   * - `disabled` - The button appears visually disabled & cannot be selected
   * - `unavailable` - The button appears "unavailable", but can still be selected
   *
   * May also need to specify `aria-label` when `"unavailable"`.
   *
   * @default 'enabled'
   */
  status?: 'enabled' | 'disabled' | 'unavailable';
  /**
   * Style of selectable button. Defaults to `'pill'` for checkbox behavior and `'square'` for radio behavior, but can be explicitly specified.
   */
  variant?: 'square' | 'pill';
  /**
   * This flag indicates whether this button is part of the 'vertical' variant of the SingleSelectButtonGroup.
   * By default, it is set to false.
   */
  isVerticalSelectButtonGroup?: boolean;
}

export type SelectableButtonProps = ExtendableSelectableBaseProps<Props>;

// The default container does nothing (returns back the contents)
const DEFAULT_CONTAINER: Container = ({ children }) => <>{children}</>;

/**
 * Selectable buttons take on the default appearance of a normal button,
 * but have a state of being "selected" or "unselected" like a radio or checkbox. They extend `SelectableBase`.
 */
const SelectableButton = ({
  'aria-label': ariaLabel,
  children,
  color = 'default',
  container: RenderContainer = DEFAULT_CONTAINER,
  disabled: disabledDeprecated,
  endIcon,
  minWidth = false,
  like,
  size,
  startIcon,
  status = 'enabled',
  variant: defaultVariant,
  isVerticalSelectButtonGroup = false,
  ...rootProps
}: SelectableButtonProps) => {
  if (size !== undefined) {
    logger.warn('`size` is deprecated and can be removed.');
  }

  const variant = defaultVariant || (like === 'checkbox' ? 'pill' : 'square');
  const screenSize = useScreenSize();
  const responsiveColor = getResponsiveValue(screenSize, color);
  const responsiveMinWidth = getResponsiveValue(screenSize, minWidth);
  const calculateMinWidth = () => {
    const defaultMinWidth = variant === 'square' ? '2.75rem' : '4rem';

    if (typeof responsiveMinWidth !== 'boolean') {
      return getCssValue(responsiveMinWidth);
    }

    return responsiveMinWidth ? defaultMinWidth : undefined;
  };
  const { style: overrideStyle, ...processedRootProps } = processStylingProps(
    rootProps,
    'SelectableButton',
    {
      stylingProps: 'warn',
      dangerousStylingProps: 'rewrite',
    },
  );
  const isDisabled = disabledDeprecated || status === 'disabled';

  if (disabledDeprecated !== undefined) {
    const mappedStatus = disabledDeprecated ? 'disabled' : 'enabled';

    logger.warn(
      `The "disabled" prop is deprecated on SelectableButton. Use \`status="${mappedStatus}"\` instead.`,
    );
  }

  // The <ButtonBase> will be rendered as a `<span>` if the `container` prop is specified.
  // Otherwise the `buttonProps` contains an `as` property which will override the `"span"`.
  return (
    <SelectableBase like={like} disabled={isDisabled} {...processedRootProps}>
      {({ input, isSelected, disabled, ...buttonProps }) => {
        const buttonStyle = {
          minWidth: calculateMinWidth(),
          ...overrideStyle,
        };
        const isUnavailable = status === 'unavailable';

        return (
          <RenderContainer
            isSelected={isSelected}
            disabled={disabled}
            aria-disabled={disabled}
            aria-label={ariaLabel}
            {...buttonProps}
          >
            <ButtonBase
              className={classNames({
                [styles['is-pill']]: variant === 'pill',
                [styles['is-square']]:
                  variant === 'square' && !isVerticalSelectButtonGroup,
                [styles['is-vertical-group']]: isVerticalSelectButtonGroup,
                [styles['is-inverse']]: responsiveColor === 'inverse',
                [styles['is-selected']]: isSelected,
                [styles['is-unavailable']]: isUnavailable,
                [styles['start-icon']]: startIcon,
                [styles['end-icon']]: endIcon,
              })}
              data-selected={isSelected ? true : undefined}
              disabled={disabled}
              style={buttonStyle}
              {...(RenderContainer === DEFAULT_CONTAINER
                ? { ...buttonProps, 'aria-label': ariaLabel }
                : { as: 'span' })}
            >
              {isUnavailable && <XMark />}
              {input}
              <DecorativeIconProvider>{startIcon}</DecorativeIconProvider>
              {children}
              <DecorativeIconProvider>{endIcon}</DecorativeIconProvider>
            </ButtonBase>
          </RenderContainer>
        );
      }}
    </SelectableBase>
  );
};

export default SelectableButton;
